import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react'
import { PageLayout } from '../components/PageLayout'
import { useAuth } from '../contexts/AuthContext'
import type { EventSummary } from '../contexts/AuthContext'
import { formatDollars, formatNumber } from '../lib/formatters'

type SortKey = 'StartDate' | 'EventName' | 'revenue' | 'guests'
type SortDir = 'asc' | 'desc'
type StatusFilter = 'all' | 'upcoming' | 'active' | 'closed'

interface EventKPI {
  revenue: number
  guests: number
  attractions: number
  rides: number
  games: number
  food: number
}

function deriveStatus(ev: EventSummary): 'upcoming' | 'active' | 'closed' {
  const now = Date.now()
  const start = Date.parse(ev.StartDate)
  const end = Date.parse(ev.EndDate)
  if (Number.isNaN(start) || Number.isNaN(end)) return 'upcoming'
  if (now < start) return 'upcoming'
  if (now > end) return 'closed'
  return 'active'
}

function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })
}

const STATUS_BADGE: Record<string, string> = {
  upcoming: 'bg-sky-50 text-sky-700 border-sky-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-50 text-slate-600 border-slate-200',
}

export function EventsPage() {
  const { events } = useAuth()
  const navigate = useNavigate()
  const [sortKey, setSortKey] = useState<SortKey>('StartDate')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [yearFilter, setYearFilter] = useState<string>('all')
  const [summaries, setSummaries] = useState<Record<number, EventKPI>>({})

  useEffect(() => {
    fetch('/api/events', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.summaries) setSummaries(d.summaries) })
      .catch(() => {})
  }, [])

  const years = useMemo(() => {
    const set = new Set<string>()
    events.forEach(e => {
      const y = new Date(e.StartDate).getFullYear()
      if (Number.isFinite(y)) set.add(String(y))
    })
    return ['all', ...Array.from(set).sort((a, b) => Number(b) - Number(a))]
  }, [events])

  const visible = useMemo(() => {
    const filtered = events.filter(ev => {
      if (statusFilter !== 'all' && deriveStatus(ev) !== statusFilter) return false
      if (yearFilter !== 'all') {
        const y = String(new Date(ev.StartDate).getFullYear())
        if (y !== yearFilter) return false
      }
      return true
    })
    return [...filtered].sort((a, b) => {
      let cmp = 0
      if (sortKey === 'StartDate') {
        cmp = Date.parse(a.StartDate) - Date.parse(b.StartDate)
      } else if (sortKey === 'EventName') {
        cmp = a.EventName.localeCompare(b.EventName)
      } else if (sortKey === 'revenue') {
        cmp = (summaries[a.EventID]?.revenue ?? 0) - (summaries[b.EventID]?.revenue ?? 0)
      } else if (sortKey === 'guests') {
        cmp = (summaries[a.EventID]?.guests ?? 0) - (summaries[b.EventID]?.guests ?? 0)
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [events, sortKey, sortDir, statusFilter, yearFilter, summaries])

  const totalRevenue = useMemo(() =>
    visible.reduce((s, ev) => s + (summaries[ev.EventID]?.revenue ?? 0), 0), [visible, summaries])
  const totalGuests = useMemo(() =>
    visible.reduce((s, ev) => s + (summaries[ev.EventID]?.guests ?? 0), 0), [visible, summaries])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir(key === 'EventName' ? 'asc' : 'desc') }
  }

  const SortIcon = ({ active }: { active: boolean }) => {
    if (!active) return null
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3" />
      : <ChevronDown className="inline h-3 w-3" />
  }

  const SortHeader = ({ label, sortBy }: { label: string; sortBy: SortKey }) => (
    <button type="button" onClick={() => toggleSort(sortBy)}
      className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[1px] text-ink-soft hover:text-butler-600">
      {label} <SortIcon active={sortKey === sortBy} />
    </button>
  )

  return (
    <PageLayout title="Events" subtitle={`${events.length} events · Butler Amusements`}>
      {/* Summary strip */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="mm-card px-4 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Events Shown</div>
          <div className="mt-0.5 text-xl font-bold text-ink-dark tabular-nums">{visible.length}</div>
        </div>
        <div className="mm-card px-4 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Total Revenue</div>
          <div className="mt-0.5 text-xl font-bold text-[#20B99D] tabular-nums">{formatDollars(totalRevenue)}</div>
        </div>
        <div className="mm-card px-4 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Total Guests</div>
          <div className="mt-0.5 text-xl font-bold text-ink-dark tabular-nums">{formatNumber(totalGuests)}</div>
        </div>
        <div className="mm-card px-4 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-[1px] text-ink-soft">Active Now</div>
          <div className="mt-0.5 text-xl font-bold text-emerald-600 tabular-nums">
            {events.filter(e => deriveStatus(e) === 'active').length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-xs text-ink-soft">
          Status:{' '}
          <select data-testid="status-filter" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as StatusFilter)}
            className="ml-1 rounded-card border border-hairline bg-white px-2 py-1 text-xs">
            <option value="all">All</option>
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="text-xs text-ink-soft">
          Year:{' '}
          <select data-testid="year-filter" value={yearFilter}
            onChange={e => setYearFilter(e.target.value)}
            className="ml-1 rounded-card border border-hairline bg-white px-2 py-1 text-xs">
            {years.map(y => <option key={y} value={y}>{y === 'all' ? 'All' : y}</option>)}
          </select>
        </label>
      </div>

      {events.length === 0 ? (
        <div className="mm-card border-dashed p-10 text-center text-sm text-ink-soft">
          <Calendar className="mx-auto mb-2 h-8 w-8 text-ink-soft/30" />
          No events found.
        </div>
      ) : (
        <div className="mm-card overflow-hidden">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-[#FAFBFB]">
                <th className="px-4 py-2.5 text-left"><SortHeader label="Event" sortBy="EventName" /></th>
                <th className="px-4 py-2.5 text-left"><SortHeader label="Date" sortBy="StartDate" /></th>
                <th className="px-4 py-2.5 text-left">
                  <span className="text-[11px] font-semibold uppercase tracking-[1px] text-ink-soft">Status</span>
                </th>
                <th className="px-4 py-2.5 text-right"><SortHeader label="Revenue" sortBy="revenue" /></th>
                <th className="px-4 py-2.5 text-right"><SortHeader label="Guests" sortBy="guests" /></th>
                <th className="px-4 py-2.5 text-right">
                  <span className="text-[11px] font-semibold uppercase tracking-[1px] text-ink-soft">Rides</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((ev, i) => {
                const status = deriveStatus(ev)
                const kpi = summaries[ev.EventID]
                return (
                  <tr key={ev.EventID}
                    onClick={() => navigate(`/events/${ev.EventID}`)}
                    className={`cursor-pointer transition-colors hover:bg-butler-600/5 ${i % 2 === 1 ? 'bg-[#FAFBFB]' : ''}`}>
                    <td className="px-4 py-3 font-medium text-ink-dark">{ev.EventName}</td>
                    <td className="px-4 py-3 text-ink-soft text-xs whitespace-nowrap">
                      {fmtDate(ev.StartDate)} — {fmtDate(ev.EndDate)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_BADGE[status]}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-ink-dark">
                      {kpi?.revenue ? formatDollars(kpi.revenue) : <span className="text-ink-soft/40">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-ink">
                      {kpi?.guests ? formatNumber(kpi.guests) : <span className="text-ink-soft/40">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-ink-soft text-xs">
                      {kpi?.attractions ? `${kpi.rides}R · ${kpi.games}G · ${kpi.food}F` : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </PageLayout>
  )
}
